import { Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';

import { EstimateDetailView } from './EstimateDetailView.js';
import { useEstimateQuery } from './useEstimateQuery.js';

/**
 * The `/estimates/$id` route component (promoted from a ComingSoon stub this
 * plan). Reads the `id` path param, fires the coarse detail query, and renders
 * the faithful `EstimateDetailView` on success. Loading → accessible `Spinner`;
 * error → announced error state with a retry (the reports/invoices 3-state
 * contract).
 *
 * The detail query reflects the latest server value on each load — D-02 approval
 * auto-converts the estimate to a job server-side; a refetch picks up the new
 * `converted` status + the converted-job link.
 */

const routeApi = getRouteApi('/estimates/$id');

export function EstimateDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const estimateId = Number(id);
  const query = useEstimateQuery(estimateId);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data.estimate) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('estimates:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('estimates:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('estimates:error.retry')}
        </button>
      </section>
    );
  }

  return (
    <EstimateDetailView
      estimate={query.data.estimate}
      fieldReport={query.data.fieldReport ?? null}
    />
  );
}
